import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import type { ReclamationSolution } from 'graphql-ts-types';
import { display } from 'styled-system';

import EcoFriendlyLabel from 'common/components/EcoFriendlyLabel';
import { Flex } from 'common/components/Flex';
import RadioCard from 'common/components/Radio/RadioCard';
import ReclamationAlert from 'common/components/Reclamation/ReclamationAlert';
import { Text } from 'common/components/Text';
import VisuallyHidden from 'common/components/VisuallyHidden';
import { useLocalization } from 'common/providers/LocaleProvider';
import { useSolutionAssigner, useSolutions } from '../hooks/solutions';
import type { BaseReclamationStepProps } from '../types/steps';
import type { SolutionsTrackingEventData } from '../utils/tracking';

export interface ReclamationStepSolutionsProps extends BaseReclamationStepProps {
  trackingEventData: SolutionsTrackingEventData;
}

const SPAREPART_REASON_ID = 'sparepart';

const UnstyledLabelContent = styled(Flex)``;

const LabelContent = (props: ComponentProps<typeof UnstyledLabelContent>) => (
  <UnstyledLabelContent as="span" flex={1} flexDirection="column" {...props} />
);

const UnstyledLabelTitle = styled(Text)<any>`
  ${display};
`;

const LabelTitle = (props: ComponentProps<typeof UnstyledLabelTitle>) => (
  <UnstyledLabelTitle
    as="span"
    color="sys.neutral.text.strong"
    display="block"
    fontSize={['fs_16', null, 'fs_18']}
    fontWeight="bold"
    lineHeight={['lh_20', null, 'lh_24']}
    {...props}
  />
);

const UnstyledLabelSubtitle = styled(LabelTitle)``;

const LabelSubtitle = (props: ComponentProps<typeof UnstyledLabelSubtitle>) => (
  <UnstyledLabelSubtitle
    fontWeight="normal"
    lineHeight={['lh_20', null, 'lh_22']}
    mt="sp_4"
    {...props}
  />
);

const UnstyledLabelDescription = styled(LabelSubtitle)``;

const LabelDescription = (props: ComponentProps<typeof UnstyledLabelDescription>) => (
  <UnstyledLabelDescription
    color="sys.neutral.text.default"
    fontSize={['fs_14', null, 'fs_16']}
    lineHeight={['lh_20', null, 'lh_22']}
    {...props}
  />
);

const ReclamationStepSolutions = ({
  type,
  reclamationId,
  itemId,
  alert,
  trackingEventData,
  setHeaderProps,
  setIsNextDisabled,
  setOnSubmit,
}: ReclamationStepSolutionsProps) => {
  const { t } = useLocalization();
  const { solutionId, solutionsContent, internalAlert, setSolutionId } = useSolutions({
    reclamationId,
    itemId,
    alert,
  });
  const [isFetching] = useSolutionAssigner({
    reclamationId,
    itemId,
    solutionId,
    trackingEventData,
    setIsNextDisabled,
    setOnSubmit,
  });

  useEffect(() => {
    setHeaderProps({
      title: solutionsContent?.title,
    });
  }, [solutionsContent]);

  return (
    <Flex
      m="sp_0"
      p="sp_0"
      as="fieldset"
      border="none"
      flexDirection="column"
      data-testid={`reclamation-step-${type}-${itemId}`}
    >
      <ReclamationAlert {...internalAlert} />

      {solutionsContent?.title && (
        <VisuallyHidden as="legend">{solutionsContent.title}</VisuallyHidden>
      )}

      {solutionsContent?.values
        ?.filter((value): value is ReclamationSolution => !!value)
        .map(({ id, title, subTitle, description, available }) => (
          <RadioCard
            key={id}
            checked={solutionId === id}
            label={title}
            value={id}
            disabled={!available || isFetching}
            onChange={setSolutionId}
            name="solution"
          >
            <LabelContent>
              {available && id === SPAREPART_REASON_ID && <EcoFriendlyLabel />}
              <LabelTitle data-testid={`solution-title-${id}`}>{title}</LabelTitle>
              <LabelSubtitle data-testid={`solution-subtitle-${id}`}>
                {available ? subTitle : ''}
              </LabelSubtitle>
              <LabelDescription data-testid={`solution-description-${id}`}>
                {available ? description : t('h24_reclamation_solution_not_available')}
              </LabelDescription>
            </LabelContent>
          </RadioCard>
        )) ?? null}
    </Flex>
  );
};

export default ReclamationStepSolutions;
